export type Point3D = {
  x: number;
  y: number;
  z: number;
};

export type Volume3D = {
  width: number;
  height: number;
  depth: number;
  data?: ArrayLike<number>;
  rescaleSlope?: number;
  rescaleIntercept?: number;
  spacing?: readonly [number, number, number];
};

export type ThickLineOptions = {
  volume?: Volume3D;
  huMin?: number;
  huMax?: number;
  thicknessUnit?: "voxel" | "world";
  spacing?: readonly [number, number, number];
};

const offsetCache = new Map<string, Int16Array>();

function validatePoint(point: Point3D, name: string): void {
  if (
    !Number.isFinite(point.x) ||
    !Number.isFinite(point.y) ||
    !Number.isFinite(point.z)
  ) {
    throw new Error(`${name} 좌표가 올바르지 않습니다.`);
  }
}

function validateVolume(volume: Volume3D): void {
  if (
    !Number.isInteger(volume.width) ||
    !Number.isInteger(volume.height) ||
    !Number.isInteger(volume.depth) ||
    volume.width <= 0 ||
    volume.height <= 0 ||
    volume.depth <= 0
  ) {
    throw new Error("volume 크기가 올바르지 않습니다.");
  }
}

function createOffsetCacheKey(
  thickness: number,
  thicknessUnit: "voxel" | "world",
  spacing: readonly [number, number, number]
): string {
  return [
    Math.round(thickness * 1000) / 1000,
    thicknessUnit,
    Math.round(spacing[0] * 1000) / 1000,
    Math.round(spacing[1] * 1000) / 1000,
    Math.round(spacing[2] * 1000) / 1000,
  ].join("|");
}

function getSphereOffsets(
  thickness: number,
  thicknessUnit: "voxel" | "world",
  spacing: readonly [number, number, number]
): Int16Array {
  const cacheKey = createOffsetCacheKey(thickness, thicknessUnit, spacing);

  const cached = offsetCache.get(cacheKey);
  if (cached) return cached;

  const radius = thickness <= 1 ? 0 : thickness / 2;

  const sx = thicknessUnit === "world" ? spacing[0] : 1;
  const sy = thicknessUnit === "world" ? spacing[1] : 1;
  const sz = thicknessUnit === "world" ? spacing[2] : 1;

  const rx = Math.ceil(radius / sx);
  const ry = Math.ceil(radius / sy);
  const rz = Math.ceil(radius / sz);

  const radiusSq = radius * radius;
  const offsets: number[] = [];

  for (let dz = -rz; dz <= rz; dz++) {
    const wz = dz * sz;
    const wzSq = wz * wz;

    for (let dy = -ry; dy <= ry; dy++) {
      const wy = dy * sy;
      const wySq = wy * wy;

      for (let dx = -rx; dx <= rx; dx++) {
        const wx = dx * sx;
        const distSq = wx * wx + wySq + wzSq;

        if (distSq <= radiusSq + Number.EPSILON) {
          offsets.push(dx, dy, dz);
        }
      }
    }
  }

  const result = Int16Array.from(offsets);
  offsetCache.set(cacheKey, result);

  return result;
}

function getOffsetBounds(offsets: Int16Array): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
} {
  let minX = 0;
  let maxX = 0;
  let minY = 0;
  let maxY = 0;
  let minZ = 0;
  let maxZ = 0;

  for (let i = 0; i < offsets.length; i += 3) {
    const dx = offsets[i];
    const dy = offsets[i + 1];
    const dz = offsets[i + 2];

    if (dx < minX) minX = dx;
    if (dx > maxX) maxX = dx;

    if (dy < minY) minY = dy;
    if (dy > maxY) maxY = dy;

    if (dz < minZ) minZ = dz;
    if (dz > maxZ) maxZ = dz;
  }

  return { minX, maxX, minY, maxY, minZ, maxZ };
}

export function getThickLineCoords3D(
  start: Point3D,
  end: Point3D,
  thickness: number = 1,
  options: ThickLineOptions = {}
): Point3D[] {
  validatePoint(start, "start");
  validatePoint(end, "end");

  if (!Number.isFinite(thickness) || thickness <= 0) {
    throw new Error("thickness는 0보다 큰 숫자여야 합니다.");
  }

  const volume = options.volume;

  if (volume) {
    validateVolume(volume);
  }

  const hasHuMin = options.huMin !== undefined;
  const hasHuMax = options.huMax !== undefined;

  if (hasHuMin !== hasHuMax) {
    throw new Error("huMin과 huMax는 함께 입력해야 합니다.");
  }

  const useHUFilter = hasHuMin && hasHuMax;

  if (useHUFilter && !volume?.data) {
    throw new Error("HU 필터를 사용하려면 options.volume.data가 필요합니다.");
  }

  const thicknessUnit = options.thicknessUnit ?? "voxel";
  const spacing = volume?.spacing ?? options.spacing ?? ([1, 1, 1] as const);

  const offsets = getSphereOffsets(thickness, thicknessUnit, spacing);

  const result: Point3D[] = [];

  const hasVolume = volume !== undefined;

  const width = volume?.width ?? 0;
  const height = volume?.height ?? 0;
  const depth = volume?.depth ?? 0;
  const sliceSize = width * height;

  const data = volume?.data;
  const slope = volume?.rescaleSlope ?? 1;
  const intercept = volume?.rescaleIntercept ?? 0;

  let rawMin = 0;
  let rawMax = 0;

  if (useHUFilter) {
    const a = (options.huMin! - intercept) / slope;
    const b = (options.huMax! - intercept) / slope;

    rawMin = Math.min(a, b);
    rawMax = Math.max(a, b);
  }

  const visited = new Set<number>();

  const offsetBounds = getOffsetBounds(offsets);

  const xStart = Math.round(start.x);
  const yStart = Math.round(start.y);
  const zStart = Math.round(start.z);

  const xEnd = Math.round(end.x);
  const yEnd = Math.round(end.y);
  const zEnd = Math.round(end.z);

  const localMinX = Math.min(xStart, xEnd) + offsetBounds.minX;
  const localMinY = Math.min(yStart, yEnd) + offsetBounds.minY;
  const localMinZ = Math.min(zStart, zEnd) + offsetBounds.minZ;

  const localMaxX = Math.max(xStart, xEnd) + offsetBounds.maxX;
  const localMaxY = Math.max(yStart, yEnd) + offsetBounds.maxY;
  const localMaxZ = Math.max(zStart, zEnd) + offsetBounds.maxZ;

  const localWidth = localMaxX - localMinX + 1;
  const localHeight = localMaxY - localMinY + 1;
  const localDepth = localMaxZ - localMinZ + 1;
  const localSliceSize = localWidth * localHeight;

  const canUseLocalNumericKey =
    Number.isSafeInteger(localWidth) &&
    Number.isSafeInteger(localHeight) &&
    Number.isSafeInteger(localDepth) &&
    Number.isSafeInteger(localWidth * localHeight * localDepth);

  const visitedStringFallback = canUseLocalNumericKey
    ? undefined
    : new Set<string>();

  const stamp = (cx: number, cy: number, cz: number): void => {
    for (let i = 0; i < offsets.length; i += 3) {
      const x = cx + offsets[i];
      const y = cy + offsets[i + 1];
      const z = cz + offsets[i + 2];

      let key: number;

      if (hasVolume) {
        if (
          (x >>> 0) >= width ||
          (y >>> 0) >= height ||
          (z >>> 0) >= depth
        ) {
          continue;
        }

        key = x + y * width + z * sliceSize;

        if (visited.has(key)) {
          continue;
        }

        if (useHUFilter) {
          const rawValue = data![key];

          if (rawValue < rawMin || rawValue > rawMax) {
            continue;
          }
        }

        visited.add(key);
        result.push({ x, y, z });
      } else {
        if (canUseLocalNumericKey) {
          key =
            x -
            localMinX +
            (y - localMinY) * localWidth +
            (z - localMinZ) * localSliceSize;

          if (visited.has(key)) {
            continue;
          }

          visited.add(key);
        } else {
          const stringKey = `${x},${y},${z}`;

          if (visitedStringFallback!.has(stringKey)) {
            continue;
          }

          visitedStringFallback!.add(stringKey);
        }

        result.push({ x, y, z });
      }
    }
  };

  let x1 = xStart;
  let y1 = yStart;
  let z1 = zStart;

  const x2 = xEnd;
  const y2 = yEnd;
  const z2 = zEnd;

  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);
  const dz = Math.abs(z2 - z1);

  const xs = x2 > x1 ? 1 : x2 < x1 ? -1 : 0;
  const ys = y2 > y1 ? 1 : y2 < y1 ? -1 : 0;
  const zs = z2 > z1 ? 1 : z2 < z1 ? -1 : 0;

  stamp(x1, y1, z1);

  if (dx >= dy && dx >= dz) {
    let p1 = 2 * dy - dx;
    let p2 = 2 * dz - dx;

    while (x1 !== x2) {
      x1 += xs;

      if (p1 >= 0) {
        y1 += ys;
        p1 -= 2 * dx;
      }

      if (p2 >= 0) {
        z1 += zs;
        p2 -= 2 * dx;
      }

      p1 += 2 * dy;
      p2 += 2 * dz;

      stamp(x1, y1, z1);
    }
  } else if (dy >= dx && dy >= dz) {
    let p1 = 2 * dx - dy;
    let p2 = 2 * dz - dy;

    while (y1 !== y2) {
      y1 += ys;

      if (p1 >= 0) {
        x1 += xs;
        p1 -= 2 * dy;
      }

      if (p2 >= 0) {
        z1 += zs;
        p2 -= 2 * dy;
      }

      p1 += 2 * dx;
      p2 += 2 * dz;

      stamp(x1, y1, z1);
    }
  } else {
    let p1 = 2 * dy - dz;
    let p2 = 2 * dx - dz;

    while (z1 !== z2) {
      z1 += zs;

      if (p1 >= 0) {
        y1 += ys;
        p1 -= 2 * dz;
      }

      if (p2 >= 0) {
        x1 += xs;
        p2 -= 2 * dz;
      }

      p1 += 2 * dy;
      p2 += 2 * dx;

      stamp(x1, y1, z1);
    }
  }

  return result;
}
